import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import Box from "@memorang/ui/src/components/Box";
import { useTranslation } from "react-i18next";
import { Text, TouchableOpacity } from "react-native";
import { useAppTheme } from "../../useAppTheme";
import type { PeriodType } from "../LeaderBoardApplet";

type FilterChipsProps = {
	selectedChip: PeriodType;
	handleSelectChip: (type: PeriodType) => void;
};

const FilterChips = ({ selectedChip, handleSelectChip }: FilterChipsProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	// Calculate text width for each item

	const getChipStyle = (index: number, isSelected: boolean) => {
		const baseStyle = {
			height: 40,
			width: 121,
			borderRadius: 0,
			gap: 8,
			paddingHorizontal: 12,
			paddingVertical: 10,
			justifyContent: "center" as const,
			alignItems: "center" as const,
			flexDirection: "row" as const,
		};

		let borderRadiusStyle = {};
		let borderStyle = {};

		// Border radius logic
		if (index === 0) {
			borderRadiusStyle = {
				borderTopLeftRadius: 20,
				borderBottomLeftRadius: 20,
			};
		}
		if (index === filterChips.length - 1) {
			borderRadiusStyle = {
				borderTopRightRadius: 20,
				borderBottomRightRadius: 20,
			};
		}

		// Border logic - only left border for first, right border for last, top/bottom for all
		if (index === 0) {
			borderStyle = {
				borderTopWidth: 1,
				borderBottomWidth: 1,
				borderLeftWidth: 1,
				borderRightWidth: 0,
			};
		} else if (index === filterChips.length - 1) {
			borderStyle = {
				borderTopWidth: 1,
				borderBottomWidth: 1,
				borderLeftWidth: 0,
				borderRightWidth: 1,
			};
		} else {
			borderStyle = {
				borderTopWidth: 1,
				borderBottomWidth: 1,
				borderLeftWidth: 0,
				borderRightWidth: 0,
			};
		}

		const styleWithColor = isSelected
			? {
					...baseStyle,
					...borderRadiusStyle,
					...borderStyle,
					backgroundColor: theme.colors.primaryContainer,
					borderColor: theme.colors.outline,
				}
			: {
					...baseStyle,
					...borderRadiusStyle,
					...borderStyle,
					backgroundColor: theme.dark
						? theme.colors.inverseSurface
						: theme.colors.white,
					borderColor: theme.colors.outline,
				};

		return styleWithColor;
	};
	const chipTypes = {
		WEEK: "week",
		ALLTIME: "all_time",
		MONTH: "month",
	} as const;
	const filterChips = [
		{
			type: chipTypes.ALLTIME as PeriodType,
			daysToReset: 0,
		},
		{
			type: chipTypes.MONTH as PeriodType,
			daysToReset: 30,
		},
		{
			type: chipTypes.WEEK as PeriodType,
			daysToReset: 5,
		},
	];
	const getDividerStyle = () => ({
		width: 1,
		height: 40,
		backgroundColor: theme.colors.outline,
	});

	return (
		<Box flexDirection="row" maxWidth="100%" justifyContent="center">
			{filterChips.map((item, index) => {
				const isSelected = selectedChip === item.type;

				return (
					<Box key={item.type} flexDirection="row">
						<TouchableOpacity
							style={getChipStyle(index, isSelected)}
							onPress={() => handleSelectChip(item.type)}
						>
							{isSelected && (
								<Icon
									name="check"
									size={16}
									color={theme.colors.onPrimaryContainer}
								/>
							)}
							<Text
								style={{
									position: "absolute",
									opacity: 0,
									fontSize: 14,
									fontWeight: "500",
								}}
							>
								{t(`leaderboard.periods.${item.type}`)}
							</Text>

							<Text
								style={{
									fontSize: 14,
									fontWeight: "500",
									color: isSelected
										? theme.colors.onPrimaryContainer
										: theme.dark
											? theme.colors.inverseOnSurface
											: theme.colors.onSurface,
									textAlign: "center",
								}}
								numberOfLines={2}
							>
								{t(`leaderboard.periods.${item.type}`)}
							</Text>
						</TouchableOpacity>

						{index < filterChips.length - 1 && (
							<Box style={getDividerStyle()} />
						)}
					</Box>
				);
			})}
		</Box>
	);
};

export { FilterChips };
